import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface FullscreenToggleProps {
  /** Whether to show only icon when in fullscreen mode */
  iconOnly?: boolean;
  /** Custom className for styling */
  className?: string;
  /** Size of the button */
  size?: 'sm' | 'md' | 'lg';
  /** Position variant for smart UX */
  variant?: 'header' | 'floating' | 'compact';
}

export const FullscreenToggle: React.FC<FullscreenToggleProps> = ({ 
  iconOnly = false, 
  className = '',
  size = 'md',
  variant = 'compact'
}) => {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Responsive size classes
  const sizeClasses = {
    sm: 'w-8 h-8 sm:w-9 sm:h-9 text-sm',
    md: 'w-10 h-10 sm:w-11 sm:h-11 text-base',
    lg: 'w-12 h-12 sm:w-14 sm:h-14 text-lg'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4 sm:w-5 sm:h-5',
    md: 'w-5 h-5 sm:w-6 sm:h-6',
    lg: 'w-6 h-6 sm:w-7 sm:h-7'
  };

  // Check if document is in fullscreen mode
  const checkFullscreen = () => {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
    setIsFullscreen(isCurrentlyFullscreen);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];

    events.forEach(event => {
      document.addEventListener(event, checkFullscreen);
    });

    // Check initial state
    checkFullscreen();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, checkFullscreen);
      });
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      // Add haptic feedback for mobile devices
      if ('vibrate' in navigator) {
        navigator.vibrate(50); // Short vibration for feedback
      }

      if (!isFullscreen) {
        // Enter fullscreen
        const element = document.documentElement;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if ((element as any).webkitRequestFullscreen) {
          await (element as any).webkitRequestFullscreen();
        } else if ((element as any).mozRequestFullScreen) {
          await (element as any).mozRequestFullScreen();
        } else if ((element as any).msRequestFullscreen) {
          await (element as any).msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.warn('Fullscreen toggle failed:', error);
    }
  };

  // Variant-specific styling with responsive design
  const getVariantClasses = () => {
    switch (variant) {
      case 'floating':
        return `
          shadow-lg hover:shadow-xl border-slate-200/70 bg-white/90 hover:bg-white
          backdrop-blur-md border-2 border-white/20
          active:scale-95 transition-all duration-200 ease-out
          hover:shadow-2xl hover:shadow-slate-200/50
          sm:shadow-xl sm:hover:shadow-2xl
        `;
      case 'header':
        return 'border-slate-200/50 bg-white/70 hover:bg-white/90';
      case 'compact':
      default:
        return 'border-slate-200/60 bg-white/60 hover:bg-white/80 shadow-sm';
    }
  };

  const buttonClasses = `
    ${sizeClasses[size]}
    inline-flex items-center justify-center rounded-xl border text-slate-600 
    transition-all duration-200 hover:border-slate-300 hover:text-slate-900 
    backdrop-blur-sm hover:shadow-md
    ${isFullscreen ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-md' : ''}
    ${getVariantClasses()}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  const iconClasses = iconSizeClasses[size];

  // Always show icon-only button in fixed position
  return (
    <button
      onClick={toggleFullscreen}
      className={`${buttonClasses} group relative overflow-hidden`}
      title={isFullscreen 
        ? t('common.exitFullscreen', 'Quitter le mode plein écran')
        : t('common.enterFullscreen', 'Mode plein écran')
      }
      aria-label={isFullscreen 
        ? t('common.exitFullscreen', 'Quitter le mode plein écran')
        : t('common.enterFullscreen', 'Mode plein écran')
      }
    >
      {/* Ripple effect for modern mobile interaction */}
      <div className="absolute inset-0 rounded-xl bg-white/20 scale-0 group-active:scale-100 transition-transform duration-150 ease-out"></div>
      {isFullscreen ? (
        // Exit fullscreen icon with relative positioning for ripple
        <svg className={`${iconClasses} relative z-10`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9v4.5M15 9h4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15v-4.5M15 15h4.5M15 15l5.5 5.5" />
        </svg>
      ) : (
        // Enter fullscreen icon with relative positioning for ripple
        <svg className={`${iconClasses} relative z-10`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      )}
    </button>
  );
};
